import { Card, Drawer, Flex, Title, Text, Divider, Select, Button, Switch } from "@mantine/core";
import { useForm } from "@mantine/form";
import { assignLicenses, getOrgsOfDistributor } from "@utils/api-communication/RestApiCli";
import React, { useEffect, useState } from "react";
import { Order, ProductItem } from "../../../../types/accountManagementTypes";
import OrganizationForm from "./OrganizationForm";
import { AssignLicensesToOrganizationsDto } from "../../../../types/licenseAssignmentTypes";
import { selectUserDistributorId } from "@utils/redux-utils/selectors/userSelectors";
import { useSelector } from "react-redux";

type AssignOrgDrawerProps = {
  opened: boolean;
  close: () => void;
  licenses: any[];
  order: Order;
};

export type OrganizationFormData = {
  customOrg: {
    name: string;
    address: string;
    email: string;
    emailsToInform: string[];
    accountType: string[];
    accountInfo: string;
  };
};

type AssignFormValues = {
  customOrg: {
    name: string;
    address: string;
    email: string;
    emailsToInform: string[];
    accountType: string[];
    accountInfo: string;
  };
  products: ProductItem[];
};

const AssignOrgDrawer: React.FC<AssignOrgDrawerProps> = (props) => {
  const { opened, close, licenses, order } = props;
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [newOrgName, setNewOrgName] = useState<string>("");
  const [selectedOrg, setSelectedOrg] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const distId = useSelector(selectUserDistributorId);
  const [alreadyAssigned, setAlreadyAssigned] = useState<boolean>(false);
  const [replaceAssignment, setReplaceAssignment] = useState<boolean>(false);

  useEffect(() => {
    if (!selectedOrgId) {
      setSelectedOrg(null);
      return;
    }
    getOrgsOfDistributor(
      { selectedOrgId },
      (resp: any) => {
        setSelectedOrg(resp.data.find((org: any) => org.orgId === selectedOrgId));
      },
      (err: any) => console.error(err),
    );
  }, [selectedOrgId]);

  const handleAssignLicenses = (values: AssignFormValues) => {
    setSubmitError(null);
    if (!licenses || licenses.length === 0) {
      setSubmitError("No licenses selected");
      return;
    }
    if (!selectedOrgId) {
      setSubmitError("Select or create an organization");
      return;
    }

    const licenseIds: string[] = licenses
      .map((l: any) => l?.licenseId)
      .filter((v: any): v is string => Boolean(v));

    // sanitize/prepare emails
    const sanitizedEmails = (values.customOrg.emailsToInform || [])
      .map((e) => e.trim())
      .filter((e) => e.length > 0);

    const isNewOrg = newOrgName === selectedOrgId;

    // make sure licenIds assigned to products
    // unassigned licenses will be added to products as { licenseId: licenseId }
    values.products = values.products || [];
    licenseIds.forEach((lid) => {
      const alreadyAssigned = values.products.find((p) => p.licenseId === lid);
      if (!alreadyAssigned) {
        values.products.push({ licenseId: lid });
      }
    });

    const orgPayload: any = isNewOrg
      ? {
          name: values.customOrg.name,
          address: values.customOrg.address,
          email: values.customOrg.email,
          emailsToInform: sanitizedEmails.length ? sanitizedEmails : [],
          accountType:
            values.customOrg.accountType && values.customOrg.accountType.length
              ? values.customOrg.accountType
              : null,
          accountInfo: values.customOrg.accountInfo || "",
          products: values.products,
        }
      : { orgId: selectedOrgId, products: values.products };

    const body: AssignLicensesToOrganizationsDto = {
      data: [
        {
          licenseIds,
          org: orgPayload,
          // notify contacts for this assignment (optional)
          emailsToInform: sanitizedEmails.length ? sanitizedEmails : [],
        },
      ],
      distId,
    };

    setSubmitting(true);

    const queryParams = {};
    if (order?._id) {
      Object.assign(queryParams, { orderId: order._id });
    }
    if (alreadyAssigned) {
      Object.assign(queryParams, { replaceAssignment: replaceAssignment });
    }
    assignLicenses(
      queryParams,
      body as AssignLicensesToOrganizationsDto,
      () => {
        setSubmitting(false);
        setAlreadyAssigned(false);
        setReplaceAssignment(false);
        close();
      },
      (err: any) => {
        console.error("assignLicenses failed", err);
        if (err?.response?.status === 409) {
          setAlreadyAssigned(true);
          setSubmitError(
            "Some licenses are already assigned. Enable replacement and submit again to replace existing assignments.",
          );
        } else {
          setSubmitError(
            err?.response?.data?.message || err?.message || "Failed to assign licenses",
          );
        }
        setSubmitting(false);
      },
    );
  };

  const form = useForm<AssignFormValues>({
    initialValues: {
      customOrg: {
        name: "",
        address: "",
        email: "",
        emailsToInform: [] as string[],
        accountType: [] as string[], // added
        accountInfo: "", // added
      },
      products: [],
    },

    validate: {
      customOrg: {
        email: (value) => (!value || /^\S+@\S+$/.test(value) ? null : "Invalid email"), // optional email
      },
    },
  });

  return (
    <form>
      <Drawer position="right" size="lg" opened={opened} onClose={close}>
        <Title order={3} mb="md">
          Customer Information
        </Title>
        <OrganizationForm
          selectedOrgId={selectedOrgId}
          onSelectedOrgIdChange={setSelectedOrgId}
          newOrgName={newOrgName}
          onNewOrgNameChange={setNewOrgName}
          getInputProps={form.getInputProps}
        />

        <Card>
          <Title order={4} mt="lg">
            Licenses
          </Title>
          {licenses?.map((item) => (
            <>
              <Divider my="sm" />
              <Flex key={item.licenseId} justify="space-between" align="center" mb="sm">
                <Text>{item.licenseId}</Text>
                {selectedOrg && selectedOrg.products && selectedOrg.products.length > 0 && (
                  <Select
                    value={
                      form.values.products.find((product) => product.licenseId === item.licenseId)
                        ?.serialNo ?? null
                    }
                    data={selectedOrg.products
                      .filter((product: any) => !!product.serialNo)
                      .map((product: any) => ({
                        value: product.serialNo,
                        label: product.hardwareModel + "-" + product.serialNo,
                      }))}
                    placeholder="Select device"
                    onChange={(value) => {
                      const products = form.values.products.filter(
                        (product) => product.licenseId !== item.licenseId,
                      );

                      if (value) {
                        const selectedProduct = selectedOrg.products.find(
                          (product: any) => product.serialNo === value,
                        );
                        products.push({
                          hardwareModel: selectedProduct?.hardwareModel,
                          serialNo: selectedProduct?.serialNo,
                          alias: selectedProduct?.alias,
                          licenseId: item.licenseId,
                          scopeName: selectedProduct?.scopeName,
                        });
                      }

                      form.setFieldValue("products", products);
                    }}
                  />
                )}
              </Flex>
            </>
          ))}
        </Card>
        {alreadyAssigned && (
          <Switch
            mt="md"
            checked={replaceAssignment}
            onChange={(event) => setReplaceAssignment(event.currentTarget.checked)}
            label="Replace existing assignments for already assigned licenses"
          />
        )}
        {submitError && (
          <Text c="red" mt="sm">
            {submitError}
          </Text>
        )}
        <Flex justify="flex-end" mt="md">
          <Button
            type="submit"
            loading={submitting}
            disabled={submitting}
            onClick={() => handleAssignLicenses(form.values)}
          >
            Submit
          </Button>
        </Flex>
      </Drawer>
    </form>
  );
};

export default AssignOrgDrawer;
